import React, { createContext, useState, useEffect } from 'react';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
    // 1. Theme State
    const [darkMode, setDarkMode] = useState(false);

    // 2. User Authentication & Token Persistence
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });

    // Derived Token: Always pulls the latest from user state or storage
    const token = user?.token || localStorage.getItem('token');

    // 3. Cart State (Persisted)
    const [cart, setCart] = useState(() => {
        const savedCart = localStorage.getItem('swift_cart');
        return savedCart ? JSON.parse(savedCart) : [];
    });

    // 4. UI Visibility States
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isAuthOpen, setIsAuthOpen] = useState(false);

    // --- SIDE EFFECTS ---

    // Sync User & Token to LocalStorage whenever user changes
    useEffect(() => {
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
            if (user.token) localStorage.setItem('token', user.token);
        }
    }, [user]);

    // Sync Cart to LocalStorage
    useEffect(() => {
        localStorage.setItem('swift_cart', JSON.stringify(cart));
    }, [cart]);

    // --- LOGIC HANDLERS ---

    const toggleDarkMode = () => setDarkMode(!darkMode);

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('swift_cart');
        setUser(null);
        setCart([]);
        setIsCartOpen(false);
        setIsAuthOpen(false);
        // Clean refresh to ensure all admin states are purged from memory
        window.location.href = "/"; 
    };

    const addToCart = (product, qty) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item => 
                    item.id === product.id ? { ...item, qty: item.qty + qty } : item
                );
            }
            return [...prev, { ...product, qty }];
        });
        setIsCartOpen(true);
    };

    return (
        <AppContext.Provider 
            value={{ 
                darkMode, 
                toggleDarkMode, 
                user, 
                setUser, 
                token, // Exported for Admin API calls
                logout,
                cart, 
                setCart, 
                addToCart, 
                isCartOpen, 
                setIsCartOpen,
                isAuthOpen,
                setIsAuthOpen
            }}
        >
            <div className={darkMode ? 'dark' : ''}>
                <div className="min-h-screen bg-white dark:bg-[#0a0a0a] text-black dark:text-white transition-colors duration-500 selection:bg-red-500 selection:text-white">
                    {children}
                </div>
            </div>
        </AppContext.Provider>
    );
};